import { PublicKey, VersionedTransaction } from '@solana/web3.js'
import { createUmi } from '@metaplex-foundation/umi-bundle-defaults'
import { mplCore, transferV1 } from '@metaplex-foundation/mpl-core'
import { publicKey } from '@metaplex-foundation/umi'
import { walletAdapterIdentity } from '@metaplex-foundation/umi-signer-wallet-adapters'
import bs58 from 'bs58'
import type { Provider } from '@reown/appkit-adapter-solana/react'
import { api } from './api'
import type { Listing, NftDetail } from './types'



function browserRpcProxyUrl() {
  const path = '/chain-builder/orchestrator/rpc'
  if (typeof window !== 'undefined') return new URL(path, window.location.origin).toString()
  return path
}

function reownWalletAdapter(address: string, provider: Provider) {
  const wallet = provider as any
  return {
    name: 'Reown Solana',
    url: typeof window !== 'undefined' ? window.location.origin : 'https://www.onlyrosenft.com',
    icon: '',
    readyState: 'Installed',
    connected: true,
    publicKey: new PublicKey(address),
    signTransaction: (transaction: any) => wallet.signTransaction(transaction),
    signAllTransactions: (transactions: any[]) => wallet.signAllTransactions
      ? wallet.signAllTransactions(transactions)
      : Promise.all(transactions.map(transaction => wallet.signTransaction(transaction))),
    signMessage: (message: Uint8Array) => wallet.signMessage(message)
  }
}

function signatureText(value: any) {
  const raw = value?.signature ?? value
  if (typeof raw === 'string') return raw
  if (raw instanceof Uint8Array) return bs58.encode(raw)
  if (Array.isArray(raw)) return bs58.encode(Uint8Array.from(raw))
  return ''
}

function bytesToBase64(bytes: Uint8Array) {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

function base64ToBytes(value: string) {
  const binary = atob(value)
  return Uint8Array.from(binary, c => c.charCodeAt(0))
}

function signedToBase64(value: any): string {
  if (!value) throw new Error('Wallet did not return a signed transaction')
  if (typeof value === 'string') return value
  if (value.transaction && value.transaction !== value) return signedToBase64(value.transaction)
  if (value.signedTransaction && value.signedTransaction !== value) return signedToBase64(value.signedTransaction)
  if (typeof value.serialize === 'function') return bytesToBase64(value.serialize())
  if (value instanceof Uint8Array) return bytesToBase64(value)
  throw new Error('Wallet returned an unsupported signed transaction format')
}

export async function ensureVerifiedWallet(walletProvider: Provider, address: string) {
  const session = await api<any>('/api/v1/platform/session').catch(() => ({ wallets: [] }))
  const wallets = session?.wallets || []
  if (wallets.some((w: any) => w.namespace === 'solana' && String(w.address).toLowerCase() === address.toLowerCase())) {
    return
  }
  if (!walletProvider?.signMessage) throw new Error('Connected Solana wallet cannot sign verification messages')
  const challenge = await api<any>('/api/v1/auth/challenge', {
    method: 'POST',
    body: JSON.stringify({ namespace: 'solana', address })
  })
  const result: any = await walletProvider.signMessage(new TextEncoder().encode(challenge.message))
  const signature = typeof result === 'string' ? result : bytesToBase64(result?.signature || result)
  await api(`/api/v1/auth/verify/solana`, {
    method: 'POST',
    body: JSON.stringify({ challenge_id: challenge.challenge_id, address, signature })
  })
}

async function signPrepared(walletProvider: Provider, transaction: string) {
  if (!walletProvider) throw new Error('Connected Solana wallet provider unavailable')
  const tx = VersionedTransaction.deserialize(base64ToBytes(transaction))
  if (walletProvider.signTransaction) return signedToBase64(await walletProvider.signTransaction(tx))
  if ((walletProvider as any).signAllTransactions) {
    const signed = await (walletProvider as any).signAllTransactions([tx])
    return signedToBase64(signed[0])
  }
  if ((walletProvider as any).request) {
    return signedToBase64(await (walletProvider as any).request({ method: 'solana_signTransaction', params: { transaction } }))
  }
  throw new Error('Connected Solana wallet cannot sign this transaction')
}

async function submitAndConfirm(walletProvider: Provider, prepared: any, onStatus?: (message: string) => void) {
  if (!prepared?.id || !prepared?.transaction) throw new Error('Rose did not return a signable marketplace transaction')
  onStatus?.('Wallet review required…')
  const signed = await signPrepared(walletProvider, prepared.transaction)
  onStatus?.('Submitting signed transaction…')
  let order = await api<any>(`/api/v1/marketplace/orders/${prepared.id}/submit`, {
    method: 'POST',
    body: JSON.stringify({ signed_transaction: signed })
  })
  for (let i = 0; i < 45; i++) {
    if (order.status === 'confirmed') return order
    if (['failed', 'expired', 'cancelled'].includes(order.status)) throw new Error(`Marketplace transaction ${order.status}`)
    onStatus?.(`Confirming transaction… ${order.signature ? String(order.signature).slice(0, 8) + '…' : ''}`)
    await new Promise(resolve => setTimeout(resolve, 2000))
    order = await api<any>(`/api/v1/marketplace/orders/${prepared.id}/refresh`, { method: 'POST' })
  }
  throw new Error('Transaction was submitted but confirmation timed out. Check the activity page before retrying.')
}

export async function buyNft(walletProvider: Provider, buyer: string, detail: NftDetail, onStatus?: (message: string) => void) {
  const listing = detail.listing
  if (!detail.assetId || !listing?.seller || !listing.priceLamports) throw new Error('This NFT does not have a live listing')
  await ensureVerifiedWallet(walletProvider, buyer)
  onStatus?.('Revalidating Magic Eden listing…')
  const prepared = await api<any>('/api/v1/marketplace/magic-eden/buy', {
    method: 'POST',
    body: JSON.stringify({
      buyer_wallet: buyer,
      delivery_wallet: buyer,
      seller_wallet: listing.seller,
      token_mint: detail.assetId,
      token_account: listing.tokenAccount || undefined,
      price_lamports: Number(listing.priceLamports),
      seller_expiry: Number(listing.sellerExpiry ?? 0),
      auction_house_address: listing.auctionHouse || undefined
    })
  })
  return submitAndConfirm(walletProvider, prepared, onStatus)
}

export async function listNft(walletProvider: Provider, seller: string, assetId: string, priceLamports: number, onStatus?: (message: string) => void) {
  await ensureVerifiedWallet(walletProvider, seller)
  onStatus?.('Preparing listing…')
  const prepared = await api<any>('/api/v1/marketplace/magic-eden/list', {
    method: 'POST',
    body: JSON.stringify({ seller_wallet: seller, token_mint: assetId, price_lamports: priceLamports, expiry: 0 })
  })
  return submitAndConfirm(walletProvider, prepared, onStatus)
}

export async function cancelListing(walletProvider: Provider, seller: string, assetId: string, listing: Listing, onStatus?: (message: string) => void) {
  if (!listing.priceLamports) throw new Error('Listing price is missing')
  await ensureVerifiedWallet(walletProvider, seller)
  onStatus?.('Revalidating listing before cancellation…')
  const prepared = await api<any>('/api/v1/marketplace/magic-eden/cancel', {
    method: 'POST',
    body: JSON.stringify({
      seller_wallet: seller,
      token_mint: assetId,
      token_account: listing.tokenAccount || undefined,
      price_lamports: Number(listing.priceLamports),
      expiry: Number(listing.sellerExpiry ?? 0),
      auction_house_address: listing.auctionHouse || undefined
    })
  })
  return submitAndConfirm(walletProvider, prepared, onStatus)
}

export async function sendCoreNft(
  walletProvider: Provider,
  owner: string,
  assetId: string,
  recipient: string,
  collection?: string | null,
  onStatus?: (message: string) => void
) {
  const target = String(recipient || '').trim()
  if (!owner) throw new Error('Connect the wallet that owns this NFT')
  if (!walletProvider) throw new Error('Connected Solana wallet provider unavailable')
  if (!(walletProvider as any).signTransaction) throw new Error('Connected Solana wallet cannot sign NFT transfers')

  let targetKey: PublicKey
  try {
    targetKey = new PublicKey(target)
  } catch {
    throw new Error('Enter a valid Solana wallet address')
  }
  if (targetKey.toBase58() === owner) throw new Error('Recipient must be a different wallet')

  await ensureVerifiedWallet(walletProvider, owner)
  onStatus?.('Preparing Core NFT transfer…')

  const umi = createUmi(browserRpcProxyUrl())
    .use(mplCore())
    .use(walletAdapterIdentity(reownWalletAdapter(owner, walletProvider) as any))

  const input: any = {
    asset: publicKey(assetId),
    newOwner: publicKey(targetKey.toBase58())
  }
  if (collection) input.collection = publicKey(collection)

  onStatus?.('Approve the NFT transfer in your wallet…')
  const result = await transferV1(umi, input).sendAndConfirm(umi)
  const signature = signatureText(result)
  onStatus?.('Transfer confirmed on Solana.')
  return { signature, recipient: targetKey.toBase58() }
}

